import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { Box, Flex, HStack } from '@chakra-ui/react';
import { addDays, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';

import { Condition } from 'Component/others';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { TextBold, TextSemiBold } from 'UiKit/typography';
import FilterDateRangePicker from 'UiKit/filter-date-range-picker';
import FilterSelect from 'UiKit/filter-select';
import { DownloadIcon, PlusIcon, TimeOutlineIcon, UploadIcon } from 'Asset/icon';
import useGetExportSchedule from 'Domain/schedule/useGetExportSchedule';
import { getMenuPermissions } from 'Helper/getMenuPermissions';
import Tooltip from 'UiKit/tooltip';
import { viewTypeOptions } from 'Const/schedule';
import getTimezoneAbbr from 'Helper/getTimezoneAbbr';
import ScheduleAlert from 'View/schedule/component/schedule-alert';
import { useScheduleState } from 'View/schedule/state';

import styles from './styles.module.css';

const Head = ({ scheduleData }) => {
  const { t } = useTranslation('schedule');
  const navigate = useNavigate();
  const cookies = new Cookies();
  const organization = cookies.get('organization');
  const isIndonesia = organization?.timezone === 'Asia/Jakarta';
  const timezone = isIndonesia ? 'Asia/Jakarta' : 'Asia/Kuala_Lumpur';

  const [currentTime, setCurrentTime] = useState(`${formatInTimeZone(new Date(), timezone, 'HH:mm')} ${getTimezoneAbbr(timezone)}`);

  const { filterState, updateState, updateFilterState } = useScheduleState();
  const { viewType, dateRange } = filterState;
  const schedulePermission = getMenuPermissions('schedule', 'schedule');

  const { mutate: exportScheduleMutation, isLoading: isExporting } = useGetExportSchedule();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(`${formatInTimeZone(new Date(), timezone, 'HH:mm')} ${getTimezoneAbbr(timezone)}`);
    }, 60 * 1000);
    return () => clearInterval(timer);
  }, [timezone]);

  const handleViewTypeChange = (option) => {
    if (!dateRange || !dateRange.startDate) return;

    const newEndDate = addDays(dateRange.startDate, option.value - 1);

    updateFilterState({
      viewType: option,
      dateRange: {
        startDate: dateRange.startDate,
        endDate: newEndDate,
      },
    });
  };

  const handleDateRangeChange = ({ startDate, endDate }) => {
    const newStartDate = startOfDay(startDate);
    const newEndDate = startOfDay(endDate);

    updateFilterState({
      dateRange: {
        startDate: newStartDate,
        endDate: newEndDate,
      },
    });
  };

  const handleUpload = () => {
    updateState({ isOpenFormUpload: true });
  };

  const handleExport = () => {
    if (!dateRange || !dateRange.startDate) return console.warn('dateRange or dateRange.startDate not available for export');

    const currentMonth = dateRange.startDate.getMonth() + 1;
    const currentYear = dateRange.startDate.getFullYear();

    exportScheduleMutation({ month: currentMonth, year: currentYear });
  };

  const handleCreateSchedule = () => {
    updateState({ isOpenFormUpsert: true });
  };

  const handleGenerateSchedule = () => {
    navigate('/schedule/smart-schedule');
  };

  const handleTodayClick = () => {
    const today = new Date();
    const startOfToday = startOfDay(today);

    // Calculate end date based on current view type
    let endDate;
    if (viewType.value === 7) {
      endDate = startOfDay(addDays(today, 6));
    } else if (viewType.value === 3) {
      endDate = startOfDay(addDays(today, 2));
    } else {
      endDate = startOfDay(addDays(today, 0));
    }

    updateFilterState({
      dateRange: {
        startDate: startOfToday,
        endDate,
      },
    });
  };

  return useMemo(
    () => (
      <Box className={styles['head__container']}>
        <Flex className={styles['head__container-flex']}>
          <TextBold className={styles['head__title-text']}>{t('title')}</TextBold>
        </Flex>

        <Condition children={<ScheduleAlert scheduleData={scheduleData} />} statement={scheduleData?.noActiveHostScheduleCount} />

        <Flex className={styles['filter__container']}>
          <Box className={styles['filter-left__container']}>
            <HStack spacing={2}>
              <Box className={styles['select__container']}>
                <FilterDateRangePicker
                  startDate={dateRange?.startDate}
                  endDate={dateRange?.endDate}
                  onChange={handleDateRangeChange}
                  viewType={viewType}
                  variant="outlined"
                />
              </Box>
            </HStack>

            <Box className={styles['select__container']}>
              <FilterSelect value={viewType} onChange={handleViewTypeChange} options={viewTypeOptions} clearable={false} />
            </Box>

            <HStack spacing={'10px'} padding={'8px 16px'}>
              <Tooltip label="Go to today" placement="top">
                <TimeOutlineIcon
                  width="18px"
                  height="18px"
                  color="var(--color-primary)"
                  onClick={handleTodayClick}
                  style={{ cursor: 'pointer' }}
                  data-testid="schedule__button-today"
                />
              </Tooltip>
              <TextSemiBold className={styles['head__time-text']}>{currentTime}</TextSemiBold>
            </HStack>
          </Box>

          <Box className={styles['filter-right__container']}>
            <Condition statement={schedulePermission?.isCanCreate}>
              <ButtonOutline
                type="button"
                className={styles['head__button--upload']}
                color="var(--color-neutral70)"
                leftIcon={<UploadIcon width="14px" height="14px" />}
                onClick={handleUpload}
              >
                {t('actionButtonUpload')}
              </ButtonOutline>
            </Condition>

            <ButtonOutline
              type="button"
              className={styles['head__button--export']}
              color="var(--color-neutral70)"
              leftIcon={<DownloadIcon width="14px" height="14px" />}
              onClick={handleExport}
              isLoading={isExporting}
              disabled={isExporting}
            >
              {t('actionButtonExport')}
            </ButtonOutline>

            <Condition statement={schedulePermission?.isCanCreate}>
              <ButtonOutline
                type="button"
                className={styles['head__button--create']}
                borderWidth="1.5px"
                leftIcon={<PlusIcon width="14px" height="14px" />}
                onClick={handleCreateSchedule}
              >
                {t('actionButtonCreate')}
              </ButtonOutline>
              <ButtonSolid
                type="button"
                className={styles['head__button--generate']}
                leftIcon={<PlusIcon width="14px" height="14px" />}
                onClick={handleGenerateSchedule}
              >
                {t('actionButtonGenerate')}
              </ButtonSolid>
            </Condition>
          </Box>
        </Flex>
      </Box>
    ),
    /* eslint-disable */
    [filterState?.viewType, filterState?.dateRange, scheduleData?.noActiveHostScheduleCount],
  );
};

export default Head;
